import { compareVersions, satisfies, compare } from 'compare-versions'

import { Product, ZipInfo } from '@/types/products.interfaces'

export function greater(v1: string | undefined, v2: string) {
  if (v1 === undefined || v2 === undefined) return false
  return compareVersions(v1, v2) > 0
}

export function greaterOrEqual(v1: string, v2: string) {
  return compareVersions(v1, v2) >= 0
}

export function lower(v1: string, v2: string) {
  return compareVersions(v1, v2) < 0
}

export function lowerOrEqual(v1: string, v2: string) {
  return compareVersions(v1, v2) <= 0
}

export function inRange(version: string, minVersion: string, maxVersion: string): boolean {
  return compare(version, minVersion, '>=') && compare(version, maxVersion, '<=');
} 

export const semVerToInt = (semVer: string): number => {
  const split_mbo_version = semVer.split('.')
  return (
    Number(split_mbo_version[0]) * 1000000 +
    Number(split_mbo_version[1]) * 1000 +
    Number(split_mbo_version[2])
  )
}

export const getLastRelease = (
  releases: ZipInfo[] | null,
  shop_version: string | undefined,
) => {
  if (releases == undefined || shop_version == undefined) return null
  const last_release_compatible_with_shop = releases.find(
    (release: ZipInfo) => {
      return satisfies(
        shop_version,
        `>=${release.compatible_from} <=${release.compatible_to}`,
      )
    },
  )

  // Not found release compatible with shop
  if (last_release_compatible_with_shop == undefined) return null
  return last_release_compatible_with_shop
}

export const isProductUpgradable = (
  product: Product,
  upgradableModules: string[] | undefined,
): boolean => {
  if (product.installed && product.technical_info) {
    if (upgradableModules)
      return upgradableModules?.includes(product.technical_info.technical_name)
    else
      return greater(
        product?.technical_info?.last_release?.product_version,
        product.installedVersion as string,
      )
  }
  return false
}

/**
 * Extract the minor version from a semver string, cleaning any pre-release tags.
 * For example, "8.1.1-beta" will return "8.1", "8" will return "8.0".
 * @param semver - The semver string (e.g., "8.1.1-beta" or "8")
 * @returns The minor version as a string (e.g., "8.1" or "8.0")
 */
export function getMinorVersion(semver: string): string {
  // Remove everything after - or + (pre-release and build metadata)
  const cleanSemver = semver.replace(/[-+].*$/, '')
  const parts = cleanSemver.split('.')
  const major = parts[0] || '0'
  const minor = parts.length > 1 ? parts[1] : '0'
  return `${major}.${minor}`
}

/**
 * Extract the patch version from a semver string, cleaning any pre-release tags.
 * For example, "8.1.1-beta" will return "8.1.1", "8" will return "8.0.0".
 * @param semver - The semver string (e.g., "8.1.1-beta" or "8")
 * @returns The patch version as a string (e.g., "8.1.1" or "8.0.0")
 */
export function getPatchVersion(semver: string): string {
  // Remove everything after - or + (pre-release and build metadata)
  const cleanSemver = semver.replace(/[-+].*$/, '')
  const parts = cleanSemver.split('.')
  const major = parts[0] || '0'
  const minor = parts.length > 1 ? parts[1] : '0'
  const patch = parts.length > 2 ? parts[2] : '0'
  return `${major}.${minor}.${patch}`
}

/**
 * Normalize a version string to a valid semver format.
 * Handles complex version formats like "7.1.33-68+0~20250707.110+debian12~1.gbp5b05bb"
 * and extracts the core semver part (major.minor.patch).
 *
 * @param version - The version string to normalize
 * @returns A normalized semver string (e.g., "7.1.33")
 * @example
 * normalizeVersion("7.1.33-68+0~20250707.110+debian12~1.gbp5b05bb") // returns "7.1.33"
 * normalizeVersion("8.1.1-beta") // returns "8.1.1"
 * normalizeVersion("9.0") // returns "9.0.0"
 */
export function normalizeVersion(version: string): string {
  if (!version) return '0.0.0'

  // Remove everything after the first - or + (pre-release and build metadata)
  const cleanVersion = version.replace(/[-+].*$/, '')

  // Split by dots and take only the first 3 numeric parts
  const parts = cleanVersion.split('.').filter(part => /^\d+$/.test(part))

  const major = parts[0] || '0'
  const minor = parts[1] || '0'
  const patch = parts[2] || '0'

  return `${major}.${minor}.${patch}`
}

export default {
  greater,
  greaterOrEqual,
  lower,
  lowerOrEqual,
  getMinorVersion,
  getPatchVersion,
  normalizeVersion,
  semVerToInt,
  getLastRelease,
  isProductUpgradable,
  inRange
}
